import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatDate, formatNumber, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import type { Client, Site } from '@/types';
import {
  Users,
  Plus,
  Search,
  Building2,
  Monitor,
  Mail,
  Phone,
  User,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Filter,
  X,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

export function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended'>('all');

  // Accordion / Expanded details state
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);

  // Add Client Modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
  });

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [loadedClients, loadedSites] = await Promise.all([
          mockApi.getClients(),
          mockApi.getSites(),
        ]);
        setClients(loadedClients);
        setSites(loadedSites);
      } catch (err) {
        toast('Failed to load clients data', 'error');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Filtered clients
  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const matchesSearch =
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.contactEmail || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.contactPhone || '').toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [clients, search, statusFilter]);

  // Aggregate stats
  const stats = useMemo(() => {
    const total = clients.length;
    const active = clients.filter((c) => c.status === 'active').length;
    const inactive = clients.filter((c) => c.status === 'inactive').length;
    const suspended = clients.filter((c) => c.status === 'suspended').length;
    const totalSites = clients.reduce((acc, c) => acc + (c.siteCount || 0), 0);
    const totalDevices = clients.reduce((acc, c) => acc + (c.deviceCount || 0), 0);

    return { total, active, inactive, suspended, totalSites, totalDevices };
  }, [clients]);

  const handleAddClient = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newClient.name.trim()) {
      toast('Client name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      await mockApi.createClient({
        name: newClient.name.trim(),
        contactName: newClient.contactName.trim(),
        contactEmail: newClient.contactEmail.trim(),
        contactPhone: newClient.contactPhone.trim(),
        status: newClient.status,
      });
      const loadedClients = await mockApi.getClients();
      setClients(loadedClients);
      setAddModalOpen(false);
      setNewClient({
        name: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        status: 'active',
      });
      toast(`Client "${newClient.name.trim()}" added successfully`);
    } catch (err: any) {
      toast(err?.message || 'Failed to create client', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedClientId(expandedClientId === id ? null : id);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'online';
      case 'inactive':
        return 'offline';
      case 'suspended':
        return 'critical';
      default:
        return 'default';
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-primary tracking-tight">Clients</h1>
            <Badge variant="default" className="text-xs font-mono">
              {clients.length} {clients.length === 1 ? 'Client' : 'Clients'}
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Manage organization clients, site allocations, contacts, and device deployment counts.
          </p>
        </div>

        <Button variant="primary" onClick={() => setAddModalOpen(true)} className="gap-2 shrink-0">
          <Plus className="h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Metrics Summary Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Total Clients</span>
            <Users className="h-3.5 w-3.5" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-text-primary">{formatNumber(stats.total)}</div>
        </div>

        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Active</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-status-online" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-status-online">{formatNumber(stats.active)}</div>
        </div>

        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Inactive</span>
            <XCircle className="h-3.5 w-3.5 text-status-offline" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-text-secondary">{formatNumber(stats.inactive)}</div>
        </div>

        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Suspended</span>
            <AlertTriangle className="h-3.5 w-3.5 text-status-critical" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-status-critical">{formatNumber(stats.suspended)}</div>
        </div>

        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Total Sites</span>
            <Building2 className="h-3.5 w-3.5 text-accent" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-text-primary">{formatNumber(stats.totalSites)}</div>
        </div>

        <div className="surface p-3.5 rounded-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-text-muted">
            <span className="text-2xs font-medium uppercase tracking-wider">Total Devices</span>
            <Monitor className="h-3.5 w-3.5 text-accent" />
          </div>
          <div className="text-xl font-bold tabular mt-2 text-text-primary">{formatNumber(stats.totalDevices)}</div>
        </div>
      </div>

      {/* Filter and Control Bar */}
      <div className="surface p-3 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-1 items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
            <input
              type="text"
              placeholder="Search clients by name, contact, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-9 text-xs"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-text-muted hidden md:inline-block" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="input-base text-xs py-1.5 w-36"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {(search || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setStatusFilter('all');
            }}
            className="text-xs text-text-muted hover:text-text-primary shrink-0"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Main Content Area */}
      {loading ? (
        <LoadingState rows={6} />
      ) : filteredClients.length === 0 ? (
        <EmptyState
          icon={<Users className="h-10 w-10 stroke-[1.5]" />}
          title={search || statusFilter !== 'all' ? 'No clients match your filters' : 'No clients created yet'}
          description={
            search || statusFilter !== 'all'
              ? 'Try adjusting your search term or status filter.'
              : 'Add your first client to start organizing sites, devices, and policies.'
          }
          action={
            search || statusFilter !== 'all' ? (
              <Button variant="secondary" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                Reset Filters
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setAddModalOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add First Client
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => {
            const clientSites = sites.filter((s) => s.clientId === client.id);
            const isExpanded = expandedClientId === client.id;

            return (
              <Card key={client.id} className="flex flex-col justify-between overflow-hidden hover:border-border-strong transition-all">
                <div className="p-4 space-y-4">
                  {/* Top Bar: Name & Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-text-primary text-base flex items-center gap-2">
                        {client.name}
                      </h3>
                      <div className="flex items-center gap-2 text-2xs text-text-muted mt-1">
                        <Clock className="h-3 w-3" />
                        <span>Added {formatDate(client.createdAt, 'MMM d, yyyy')}</span>
                      </div>
                    </div>

                    <Badge variant={getStatusBadgeVariant(client.status)} dot>
                      <span className="capitalize">{client.status}</span>
                    </Badge>
                  </div>

                  {/* Contact Info Card */}
                  <div className="surface-elevated p-3 rounded-md space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <User className="h-3.5 w-3.5 text-text-muted shrink-0" />
                      <span className="font-medium text-text-primary truncate">{client.contactName}</span>
                    </div>

                    <div className="flex items-center gap-2 text-text-muted">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <a href={`mailto:${client.contactEmail}`} className="hover:text-accent truncate transition-colors">
                        {client.contactEmail}
                      </a>
                    </div>

                    <div className="flex items-center gap-2 text-text-muted">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{client.contactPhone}</span>
                    </div>
                  </div>

                  {/* Site & Device Count Summary */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div className="surface-elevated p-2.5 rounded text-center border border-border/50">
                      <div className="flex items-center justify-center gap-1.5 text-text-muted text-2xs uppercase tracking-wider font-medium">
                        <Building2 className="h-3 w-3 text-accent" />
                        <span>Sites</span>
                      </div>
                      <div className="text-lg font-bold tabular text-text-primary mt-1">
                        {client.siteCount ?? clientSites.length}
                      </div>
                    </div>

                    <div className="surface-elevated p-2.5 rounded text-center border border-border/50">
                      <div className="flex items-center justify-center gap-1.5 text-text-muted text-2xs uppercase tracking-wider font-medium">
                        <Monitor className="h-3 w-3 text-status-online" />
                        <span>Devices</span>
                      </div>
                      <div className="text-lg font-bold tabular text-text-primary mt-1">
                        {formatNumber(client.deviceCount || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer / Expand Sites Toggle */}
                <div className="bg-base-elevated/40 border-t border-border px-4 py-2.5 flex items-center justify-between">
                  <button
                    onClick={() => toggleExpand(client.id)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors font-medium"
                  >
                    <span>{isExpanded ? 'Hide Sites' : `View Sites (${clientSites.length})`}</span>
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/sites?client=${client.id}`)}
                    className="text-2xs h-6 px-2 text-accent hover:text-accent-hover gap-1"
                  >
                    <span>Manage Sites</span>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>

                {/* Expandable Sites Accordion Panel */}
                {isExpanded && (
                  <div className="border-t border-border bg-base-hover/30 p-3 space-y-2 animate-fade-in text-xs">
                    <div className="flex items-center justify-between text-2xs text-text-muted font-medium uppercase tracking-wider px-1">
                      <span>Client Sites</span>
                      <span>Devices</span>
                    </div>

                    {clientSites.length === 0 ? (
                      <div className="text-center py-4 text-text-muted italic text-xs">
                        No sites configured for this client.
                      </div>
                    ) : (
                      clientSites.map((site) => (
                        <div
                          key={site.id}
                          className="surface p-2.5 rounded flex items-center justify-between gap-2 hover:border-border-strong transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-text-primary truncate">{site.name}</div>
                            <div className="flex items-center gap-1 text-2xs text-text-muted mt-0.5">
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{site.city}, {site.country}</span>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold tabular text-text-secondary">
                              <Monitor className="h-3 w-3 text-status-online" />
                              {site.deviceCount || 0}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Client Modal */}
      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add New Client"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="primary" loading={saving} onClick={() => handleAddClient()}>
              Create Client
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddClient} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Client / Organization Name <span className="text-status-critical">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Acme Corporation"
              value={newClient.name}
              onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
              className="input-base"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Contact Person
              </label>
              <input
                type="text"
                placeholder="e.g. John Doe"
                value={newClient.contactName}
                onChange={(e) => setNewClient({ ...newClient, contactName: e.target.value })}
                className="input-base"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Initial Status
              </label>
              <select
                value={newClient.status}
                onChange={(e) => setNewClient({ ...newClient, status: e.target.value as any })}
                className="input-base"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Contact Email
            </label>
            <input
              type="email"
              placeholder="e.g. it-admin@acme.com"
              value={newClient.contactEmail}
              onChange={(e) => setNewClient({ ...newClient, contactEmail: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              placeholder="e.g. +1 (555) 234-5678"
              value={newClient.contactPhone}
              onChange={(e) => setNewClient({ ...newClient, contactPhone: e.target.value })}
              className="input-base"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
